import type { components as AccountlessApiComponents } from './generated/accountless-natalie-api';
import type { components as NatalieApiComponents } from './generated/natalie-api';
import type { PaginatedApiParamsV3 } from 'domain/Common';

// Re-export the generated enum type as a const enum for runtime use
export const NatalieEventStatus = {
  Active: 'ACTIVE',
  Completed: 'COMPLETED',
} as const;

export type NatalieEventStatus = NatalieApiComponents['schemas']['NatalieEventStatus'];

// --- API response types (generated from OpenAPI spec) ---

/** Full event DTO returned by natalie-api */
export type NatalieEventDto = NatalieApiComponents['schemas']['NatalieEventDto'];

/** Audience persona on an event ({ type: 'user' | 'buyer' }, extensible) */
export type Persona = NatalieApiComponents['schemas']['Persona'];
export type PersonaType = Persona['type'];

/** Explore use-case card on an event ({ icon, title, body }) */
export type ExploreUseCase = NatalieApiComponents['schemas']['ExploreUseCase'];

/** Create/update request body */
export type NatalieEventCreateDto = NatalieApiComponents['schemas']['NatalieEventCreateDto'];

/** LiveKit token response */
export type GetLiveKitTokenData = NatalieApiComponents['schemas']['TokenResponse'];

/** Accountless event info response */
export type AccountlessNatalieEventApiResponse = AccountlessApiComponents['schemas']['EventInfoResponse'];

// --- Frontend query param types ---

export type GetNatalieEventListApiParams = PaginatedApiParamsV3 & {
  communityId: number;
  statuses: NatalieEventStatus[];
};

/** Alias used by list views — same shape as the API DTO */
export type NatalieEventListItem = NatalieEventDto;

// --- Frontend-derived detail types (mapped from API DTO) ---

/** Input type for the public-event mapper (API response shape) */
export type NatalieEventPublicDetailData = NatalieEventDto;

/** Input type for the authenticated-event mapper (API response shape) */
export type NatalieEventDetailData = NatalieEventDto;

/** Mapped public event detail used by UI components */
export type NatalieEventPublicDetail = {
  id: string;
  communityId: string;
  endDate: string;
  name: string;
  description: string | null;
  topic: string;
  ownerName: string;
  communityName: string;
  status: NatalieEventStatus;
  communityLogo: string | null;
  videoUrl: string | null;
  durationInMinutes: number;
  /** Audience persona; defaults to { type: 'user' } for legacy events. */
  persona: Persona;
  /** Event-specific explore use-case cards; defaults to [] for legacy events. */
  exploreUseCases: ExploreUseCase[];
  /**
   * 12-char test-link invite code. Lets the Guest landing recognise a test link
   * (its URL invite-code position is shared by the real and test code). Null when
   * the event has no test link or the (accountless) endpoint does not expose it.
   */
  testInviteCode: string | null;
};

/** Mapped authenticated event detail used by UI components */
export type NatalieEventDetail = NatalieEventPublicDetail;

// --- Frontend-only types (not in OpenAPI spec) ---

export type CreateNatalieEventApiParams = {
  communityId: number;
  name: string;
  description?: string | null;
  topic: string;
  sessionDurationMinutes: number;
  endDate: string;
  status: NatalieEventStatus;
  videoUrl?: string;
};

export type UpdateNatalieEventApiParams = CreateNatalieEventApiParams & {
  id: string;
};

export type RegisterToNatalieEventArgs = {
  id: string;
  /**
   * When true, suppresses navigation to join page and MemberSetup dialog.
   * Used for magic link auth flow where the user is auto-registered silently.
   */
  silent?: boolean;
};

// LiveKit token query params (frontend-specific, includes debug/agentName)
export type GetLiveKitTokenParams = {
  eventId: string;
  version?: string;
  slot?: string;
  mode?: 'voice' | 'text';
  agentName?: string;
  debug?: boolean;
  /**
   * 12-char test-link invite code from the Registered test Room URL. When
   * present the server resolves source=test and returns `isTest=true`; the
   * client never asserts test-ness itself.
   */
  testInviteCode?: string;
};

// Accountless token params for POST /natalie/{eventId}/token/{inviteCode}
export type GetAccountlessLiveKitTokenParams = {
  eventId: string;
  inviteCode: string;
  /**
   * Optional agent worker override (e.g. a preview deployment), sent as
   * agentConfig.agentName. Absent → the backend's default AGENT_NAME is used.
   */
  agentName?: string;
};

// Args for POST /natalie/{eventId}/visit — the conversion funnel's "visited" signal.
export type RecordAccountlessVisitArgs = {
  eventId: string;
  /** Client-minted (localStorage) visitor id; analytics-only, spoofable — not a security control. */
  visitorId: string;
  /** Backend account id, present only for authed visitors (numeric string); analytics-only. */
  accountId?: string;
};

// --- Event metrics (GET /natalie/{eventId}/metrics, EP13) ---

/**
 * A single day in the conversation timeline. `bucket` is a `YYYY-MM-DD` date
 * (server buckets by the day a conversation started).
 */
export type NatalieTimelineBucket = {
  bucket: string;
  conversationCount: number;
  avgDurationSeconds: number;
};

/**
 * Per-event conversation metrics over REAL (non-test) records, as a narrowing funnel:
 * `openedCount` (someone opened the link) ⊇ `roomsCreatedCount` (a room existed) ⊇
 * `conversationCount` (the user actually spoke). The gap between the last two is rooms that
 * opened and were abandoned before a real conversation started. The wider steps are typed
 * because the response carries them, but only `conversationCount` is displayed: the funnel is
 * an operational detail, not something the event owner is shown.
 * `completionRate` (a fraction 0–1), `avgDurationSeconds` and `timeline` cover the counted
 * conversations only. `invitedCount` is set manually via EP14 and defaults to 0 (there is no
 * true "unset").
 */
export type NatalieEventMetrics = {
  invitedCount: number;
  openedCount: number;
  roomsCreatedCount: number;
  conversationCount: number;
  completionRate: number;
  avgDurationSeconds: number;
  timeline: NatalieTimelineBucket[];
};

/** Args for PUT /natalie/{eventId}/invited-count (EP14). */
export type SetNatalieInvitedCountArgs = {
  eventId: string;
  invitedCount: number;
};

// --- Conversations (GET /natalie/conversations, EP12) ---

/**
 * A single conversation-lifecycle record. Only the fields the frontend uses are
 * typed; the server record has more. `startedAt`/`endedAt` are ISO-8601.
 * `_source`, `lkRoomSid` and `endedAt` drive the "engaged" classification, and
 * `turnCount` the "the user spoke" one, which together mirror the server's metrics
 * aggregation.
 */
export type NatalieConversationRecord = {
  sessionId: string;
  eventId: string;
  startedAt: string;
  endedAt?: string;
  durationSeconds?: number;
  source?: 'test';
  engaged?: boolean;
  lkRoomSid?: string;
  _source?: string;
  /** User turns committed. Only carried on the agent write path, so absent means
   * "speech unconfirmed", which does not count as a conversation. */
  turnCount?: number;
};

/** One page of EP12 results. `nextCursor` is absent on the last page. */
export type NatalieConversationsPage = {
  items: NatalieConversationRecord[];
  nextCursor?: string;
};

/** Args for GET /natalie/conversations (EP12). */
export type GetNatalieConversationsArgs = {
  eventId: string;
  cursor?: string;
};

/** Body schema for POST /natalie/{eventId}/{inviteCode}/reminder */
export type ScheduleAccountlessReminderBody = AccountlessApiComponents['schemas']['ScheduleReminderRequest'];

export type ScheduleAccountlessReminderArgs = ScheduleAccountlessReminderBody & {
  eventId: string;
  inviteCode: string;
};
